import axios, { AxiosError } from 'axios'

interface ValidationDetail {
  msg?: unknown
}

const isDetailArray = (value: unknown): value is ValidationDetail[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'object' && entry !== null)

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== ''

const CONFLICT_STATUS = 409

/**
 * Whether the API refused because something it would create already exists.
 *
 * Says only that, and on its own it does not say whether a retry is worth
 * offering — an endpoint with more than one conflict to report names which one
 * it hit, and `getApiErrorCode` is what reads that. Where a conflict has a
 * single meaning, the status alone is the whole answer.
 */
export const isConflictError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === CONFLICT_STATUS

/**
 * Whether the client gave up waiting rather than the request failing.
 *
 * The one failure that says nothing about what the server did: a bounded write
 * that times out may still have committed.
 */
export const isTimeoutError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  (error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ETIMEDOUT)

/**
 * The machine-readable code naming which refusal the API returned, if it named one.
 *
 * Endpoints that can refuse for more than one reason at the same status send a
 * `code` beside the message, so a caller can act on the difference — one refusal
 * a corrected field will get past, another that nothing will — without matching
 * on prose. Absent for every refusal with only one meaning.
 */
export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined
  }
  const data: unknown = error.response?.data
  if (typeof data !== 'object' || data === null) {
    return undefined
  }
  const { code } = data as { code?: unknown }
  return isNonEmptyString(code) ? code : undefined
}

/**
 * Pulls a human-readable message out of an API error.
 *
 * The backend replies in two different shapes: `ServiceError` (business rules —
 * duplicate name, conflict) returns `{ message }`, while `HTTPException` and
 * FastAPI request validation return `{ detail }` — a string for the former, an
 * array of per-field errors for the latter. Anything unrecognised (network
 * failure, non-Axios throw) falls back to the caller's message.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) {
    // A plain Error thrown by our own code already carries a message written
    // for the user. Axios errors are excluded because theirs are transport-level
    // ("Network Error"), which reads worse than the caller's fallback.
    return error instanceof Error && isNonEmptyString(error.message)
      ? error.message
      : fallback
  }

  const data: unknown = error.response?.data
  if (typeof data !== 'object' || data === null) {
    return fallback
  }

  const { message, detail } = data as { message?: unknown; detail?: unknown }

  if (isNonEmptyString(message)) {
    return message
  }
  if (isNonEmptyString(detail)) {
    return detail
  }
  if (isDetailArray(detail)) {
    const messages = detail.map((entry) => entry.msg).filter(isNonEmptyString)
    if (messages.length > 0) {
      return messages.join('; ')
    }
  }

  return fallback
}
